import React, { useState, useEffect, useRef, useCallback } from 'react';
import SinFichero from './SinFichero';
import { getSignedUrl } from '../../../../../services/s3Service';

const DocumentMessage = ({ details }) => {
  const { documentUrl, maytapiDocumentUrl, s3Key, filename, mimeType, caption } = details;
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState(documentUrl);
  const isRenewingRef = useRef(false);
  const retryCountRef = useRef(0);

  // Function to renew S3 signed URL
  const renewSignedUrl = useCallback(async () => {
    if (!s3Key || isRenewingRef.current) return null;

    isRenewingRef.current = true;
    try {
      const signedUrl = await getSignedUrl(s3Key);
      if (signedUrl) {
        retryCountRef.current = 0; // Reset retry count on success
        isRenewingRef.current = false;
        return signedUrl;
      }
    } catch (error) {
      console.error('Error renewing signed URL:', error);
    }
    isRenewingRef.current = false;
    return null;
  }, [s3Key]);

  useEffect(() => {
    // If we have an S3 key, always try to get a fresh signed URL
    // This ensures we have a valid URL even if the initial one is expired
    if (s3Key) {
      const initializeUrl = async () => {
        const freshUrl = await renewSignedUrl();
        if (freshUrl) {
          setCurrentDocumentUrl(freshUrl);
        } else if (maytapiDocumentUrl) {
          // Fallback to MayTapi URL only if we couldn't get S3 URL
          setCurrentDocumentUrl(maytapiDocumentUrl);
        } else if (documentUrl) {
          // Use original documentUrl as last resort
          setCurrentDocumentUrl(documentUrl);
        }
      };
      initializeUrl();
    } else if (maytapiDocumentUrl) {
      // Fallback to MayTapi URL if no S3 key
      setCurrentDocumentUrl(maytapiDocumentUrl);
    } else if (documentUrl) {
      // Use original documentUrl as last resort
      setCurrentDocumentUrl(documentUrl);
    }
  }, [s3Key, renewSignedUrl, maytapiDocumentUrl, documentUrl]);

  const handleLinkClick = async e => {
    // When clicking the link, verify the URL is still valid
    // If we have an s3Key, try to renew the URL before opening
    if (s3Key && currentDocumentUrl) {
      // Check if URL might be expired by trying to renew it
      const renewedUrl = await renewSignedUrl();
      if (renewedUrl && renewedUrl !== currentDocumentUrl) {
        e.preventDefault();
        setCurrentDocumentUrl(renewedUrl);
        // Open the new URL
        window.open(renewedUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  // Sin fichero NO se devuelve null: eso hacía desaparecer del chat el mensaje
  // entero, y un justificante de pago que no se ve es peor que uno roto.
  if (!currentDocumentUrl) return <SinFichero details={details} />;

  return (
    <div className='space-y-1'>
      <a
        href={currentDocumentUrl}
        target='_blank'
        rel='noreferrer'
        className='text-sm text-blue-600 hover:underline break-all'
        onClick={handleLinkClick}
      >
        {filename || 'Documento'}
      </a>
      {caption && <div className='text-xs text-gray-600 whitespace-pre-wrap break-words'>{caption}</div>}
      <div className='text-[11px] text-gray-400'>{mimeType}</div>
    </div>
  );
};

export default DocumentMessage;
